import type { IMachineSensor } from "@dmca/models/interface/machine-sensor";
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  type PaperProps,
} from "@mui/material";
import { mergeSx, useLocalStorage } from "#hooks/index";
import { Suspense, lazy } from "react";
const MachineSensorPowerDrawHistoryLineGraph = lazy(
  () => import("./MachineSensorPowerDrawHistoryLineGraph")
);
const MachineSensorVAHistoryBarGraph = lazy(
  () => import("./MachineSensorVAHistoryBarGraph")
);
import { Button } from "#components/button";
import type { DateTime } from "@dmca/utils/luxon";

export interface MachineSensorGraphSectionProps extends PaperProps {
  devices: IMachineSensor[];
  timeRange: {
    start: number | DateTime;
    end: number | DateTime;
  };
}

export const MachineSensorGraphSection: React.FC<
  MachineSensorGraphSectionProps
> = ({ devices, sx, timeRange, ...rest }) => {
  const [graphType, setGraphType] = useLocalStorage<"power" | "vaHistory">(
    "smartcell-machine-sensor-graph-type",
    "vaHistory"
  );

  return (
    <Paper
      sx={mergeSx(
        {
          width: "100%",
          pt: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        },
        sx
      )}
      {...rest}
    >
      <Typography
        variant="subtitle2"
        sx={{ display: "block", textAlign: "center" }}
      >
        {graphType === "power" ? "Power Usage" : "30 day efficiency history"}
      </Typography>
      <Suspense
        fallback={
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              minHeight: "100px",
              width: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <Box sx={{ height: "50vh" }}>
          {graphType === "power" ? (
            <MachineSensorPowerDrawHistoryLineGraph
              sensors={devices}
              timeRange={timeRange}
            />
          ) : (
            <MachineSensorVAHistoryBarGraph sensors={devices} />
          )}
        </Box>
      </Suspense>
      <Box sx={{ mx: 1, mb: 1 }}>
        <Button
          fullWidth
          onClick={() =>
            setGraphType((old) => (old === "power" ? "vaHistory" : "power"))
          }
        >{`View ${
          graphType === "power" ? "VA Time History" : "Power Draw"
        } Graph`}</Button>
      </Box>
    </Paper>
  );
};

export default MachineSensorGraphSection;
